import { z } from 'zod';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';

const postalCodeSchema = z
  .string()
  .min(1, "郵便番号は必須です。")
  .refine((v) => /^\d{7}$/.test(v), "郵便番号は7桁で入力してください。");

const phoneSchema = z
  .string()
  .min(1, "電話番号は必須です。")
  .max(13, "電話番号は13文字以下で入力してください。");

/** 拠点名カナ: Katakana only, max 100. Spec: 拠点名カナの書式が正しくありません。 / 拠点名カナは100文字以下にしてください。 */
const locationNameKanaSchema = z
  .string()
  .max(100, "拠点名カナは100文字以下にしてください。")
  .refine((v) => !v || /^[ァ-ヶｦ-ﾟー 　]+$/u.test(v), "拠点名カナの書式が正しくありません。")
  .optional()
  .or(z.literal(""));

export const clinicLocationFormSchema = z
  .object({
    clinic_id: z
      .number({ required_error: "治療院IDは必須です。" })
      .min(1, "治療院IDは必須です。"),
    service_type: z
      .string()
      .refine(
        v => v === SERVICE_TYPE_A2 || v === SERVICE_TYPE_R2 || v === SERVICE_TYPE_BOTH,
        {
          message: 'サービス区分は必須です。',
        }
      ),
    location_name: z.string().min(1, "拠点名は必須です。").max(100, "拠点名は100文字以下にしてください。"),
    location_name_kana: locationNameKanaSchema,
    available_from: z.string().min(1, "適用開始日は必須です。"),
    available_to: z.string().optional(),
    health_center_regist_type_id: z
      .number({ required_error: "保健所登録区分は必須です。" })
      .min(1, "保健所登録区分は必須です。"),
    postal_code: postalCodeSchema,
    address1: z.string().min(1, "住所1は必須です。").max(100, "住所1は100文字以下で入力してください。"),
    address2: z.string().min(1, "住所2は必須です。").max(100, "住所2は100文字以下で入力してください。"),
    address3: z.string().optional(),
    telephone: phoneSchema,
    fax: z
      .string()
      .max(13, "FAXは13文字以下で入力してください。")
      .optional()
      .or(z.literal("")),
    rezept_address: z.string().max(100, "レセプト表示住所は100文字以下にしてください。").optional(),
    base_name: z.string().max(100, "住所拠点名は100文字以下にしてください。").optional(),
    is_compnay: z.union([z.literal(0), z.literal(1)]).nullable().optional(),
    founder_name: z.string().max(100, "開設者名は100文字以下にしてください。").optional(),
    founder_name_kana: z
      .string()
      .max(100, "開設者名カナは100文字以下にしてください。")
      .refine((v) => !v || /^[ァ-ヶｦ-ﾟー 　]+$/u.test(v), "開設者名カナの書式が正しくありません。")
      .optional()
      .or(z.literal("")),
    founder_postal_code: z
      .string()
      .optional()
      .refine((v) => !v || /^\d{7}$/.test(v), "開設者郵便番号は7桁で入力してください。"),
    founder_address1: z.string().max(100, "開設者住所1は100文字以下にしてください。").optional(),
    founder_address2: z.string().max(100, "開設者住所2は100文字以下にしてください。").optional(),
    founder_address3: z.string().max(100, "開設者住所3は100文字以下にしてください。").optional(),
    founder_date_of_birth: z.string().optional(),
    founder_telephone: z
      .string()
      .max(13, "開設者電話番号は13文字以下で入力してください。")
      .optional()
      .or(z.literal("")),
  })
  .refine((data) => !data.available_to || data.available_to >= data.available_from, {
    message: "有効期限（まで）は（から）以降の日付を指定してください。",
    path: ["available_to"],
  })
  .refine(
    (data) =>
      data.is_compnay === undefined || data.is_compnay === null || data.is_compnay === 0 || data.is_compnay === 1,
    {
    message: "法人個人を選択してください。",
    path: ["is_compnay"],
  });

export type ClinicLocationFormData = z.infer<typeof clinicLocationFormSchema>;
